import { useState, type ReactNode } from 'react'
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Button } from './Button.tsx'

export interface ErrorStateProps {
  title?: ReactNode
  /** Alias for title */
  message?: string
  description?: ReactNode
  /** Technical details – rendered as collapsible section. */
  details?: string | null
  onRetry?: () => unknown
  retryLabel?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Standard-Fehleranzeige. Details sind aufklappbar, damit Fehler im UI
 * sichtbar sind (siehe CLAUDE.md-Debugging-Regel).
 */
export function ErrorState({
  title,
  message,
  description,
  details,
  onRetry,
  retryLabel = 'Erneut versuchen',
  className,
  size = 'md',
}: ErrorStateProps) {
  const heading = title ?? message ?? 'Fehler beim Laden'
  const [open, setOpen] = useState(false)
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center gap-3',
        size === 'sm' && 'py-4',
        size === 'md' && 'py-8',
        size === 'lg' && 'py-14',
        className,
      )}
    >
      <div
        className={cn(
          'rounded-full bg-red-50 text-red-600 flex items-center justify-center',
          size === 'sm' && 'h-9 w-9 [&>svg]:h-4 [&>svg]:w-4',
          size === 'md' && 'h-12 w-12 [&>svg]:h-5 [&>svg]:w-5',
          size === 'lg' && 'h-16 w-16 [&>svg]:h-7 [&>svg]:w-7',
        )}
      >
        <AlertCircle />
      </div>
      <div className="text-body font-medium text-gray-900">{heading}</div>
      {description ? (
        <div className="text-small text-gray-600 max-w-md">{description}</div>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
      {details ? (
        <div className="w-full max-w-lg text-left">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-caption text-gray-500 hover:text-gray-700"
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Technische Details
          </button>
          {open ? (
            <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-gray-50 border border-gray-200 p-2 text-caption font-mono text-gray-700">
              {details}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
